
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { IProduct } from "@/models/products";
import { IProvider } from "@/models/providers";
import { OrgFeatureKeys } from "@/data/constants";
import RequestPromises from "@/mixins/requestPromises";
import {
  IUpgradePackagePayload,
  IUpmindUpgradeProviderData,
  IUpmindUpgradeProviderMethods
} from "@/models/providers/upmind";
import { ProductTypes } from "@/data/enums/products";
import { mapGetters } from "vuex";
import { IContractProduct } from "@/models/contracts";
import _ from "@/boot/lodash";
import { IInvoice } from "@/models/invoices";
import { ConfirmModalModes } from "@/data/enums";
import { UpmindClientRoutes } from "@/data/enums/router";

@Component({
  computed: {
    ...mapGetters({
      currentLicense: "org/currentLicense",
      hasFreeLicense: "org/hasFreeLicense"
    })
  }
})
export default class UpmindUpgradesProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ default: () => [] })
  public readonly missingKeys!: OrgFeatureKeys[];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public availableUpgrades: IProduct[] = [];
  public currentLicense!: IContractProduct;
  public hasFreeLicense!: boolean;
  public hasRequestError = false;
  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public packagesByFeature: { [key in OrgFeatureKeys]?: IProduct[] } = {};
  public dryRunInvoice: IInvoice | null = null;

  // Computed

  public get availableLicenses() {
    return _.filter(
      this.availableUpgrades,
      ({ product_type }) => product_type === ProductTypes.SINGLE_PRODUCT
    );
  }

  public get availableOptions() {
    return _.filter(
      this.availableUpgrades,
      ({ product_type }) => product_type === ProductTypes.PRODUCT_OPTION
    );
  }

  public get featureFlags() {
    return this.missingKeys.map(key => key.split(".").at(-1) || "");
  }

  public get missingKeyPackages() {
    const missingPackages = _.flatten(
      _.filter(this.packagesByFeature, (f, key) =>
        this.featureFlags.includes(key as OrgFeatureKeys)
      )
    );
    return _.uniqBy(missingPackages, "id");
  }

  public get currentLicenseOptionIds() {
    return _.map(this.currentLicense?.options, o => o.product_id);
  }

  // Life-cycle methods

  public async created() {
    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getAvailableUpgrades()
        // this.getPackagesByFeature()
      ]);
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      if (is403) this.$emit("on403");
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public async getAvailableUpgrades() {
    this.availableUpgrades = await this.$store.dispatch(
      "org/getPackageUpgrades"
    );
  }

  public async getPackagesByFeature() {
    this.packagesByFeature = await this.$store.dispatch(
      "org/getPackagesByFeature"
    );
  }

  public async confirmUpgrade(
    payload: IUpgradePackagePayload,
    licenseName: IProduct["name"]
  ): Promise<boolean> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async isUpgraded => {
      try {
        this.isProcessing = true;
        const dryRunPayload = { ...payload, dry_run: true };
        const {
          product_upgrade_quantity,
          tax_amount,
          tax_amount_formatted,
          total_amount_formatted
        }: IInvoice = await this.$store.dispatch(
          "org/upgradePackage",
          dryRunPayload
        );
        const confirm = await this.$store.dispatch("ui/open/confirmModal", {
          config: {
            props: {
              autoClose: false,
              confirmButtonText: this.$t("_action.yes_upgrade_now"),
              mode: ConfirmModalModes.BUTTON,
              title: this.$t("_action.confirm_upgrade"),
              type: "is-primary",
              message: {
                render: (h: CreateElement) =>
                  h("div", {}, [
                    h(
                      "p",
                      {},
                      this.$t("_sentence.upmind.confirm_upgrade_action", {
                        licenseName: licenseName.toLocaleLowerCase(),
                        totalSummary: tax_amount
                          ? this.$t("_.total_including_x_in_taxes", {
                              total: total_amount_formatted,
                              tax: tax_amount_formatted
                            })
                          : total_amount_formatted,
                        userCountSummary: this.$tc(
                          "_plurals.staff_users",
                          product_upgrade_quantity
                        )
                      }) as string
                    ),
                    tax_amount
                      ? h(
                          "p",
                          {
                            class: "is-size-7 has-text-grey has-margin-top-100"
                          },
                          [
                            h(
                              "em",
                              `* ${this.$t(
                                "_sentence.tax.calculation_source_msg"
                              )}`
                            )
                          ]
                        )
                      : undefined
                  ])
              }
            },
            events: {
              close: () => isUpgraded(false),
              confirmed: async () => {
                try {
                  // Do upgrade
                  const invoice = await this.$store.dispatch(
                    "org/upgradePackage",
                    payload
                  );
                  // Handle success
                  this.handleUpgradeSuccess(invoice);
                  // Resolve promise
                  isUpgraded(true);
                } catch (error) {
                  this.$store.dispatch("api/handleError", error);
                  isUpgraded(false);
                } finally {
                  confirm.close();
                }
              }
            }
          }
        });
      } catch (error) {
        this.$store.dispatch("api/handleError", error);
        isUpgraded(false);
      } finally {
        this.isProcessing = false;
      }
    });
  }

  private handleUpgradeSuccess(invoice: IInvoice) {
    Promise.all([
      // Here we refresh user data to get latest org changes (eg. upmind_current_package)
      this.$store.dispatch("auth/admin/getUser"),
      // Redirect to invoice
      this.$router
        .push({
          name: UpmindClientRoutes.INVOICE,
          params: {
            clientId: invoice.client_id,
            invoiceId: invoice.id
          }
        })
        .catch(err => err)
    ]);

    // Show toast
    this.$buefy.snackbar.open({
      message: this.$t("_sentence.upmind.upgrade_invoice_created") as string,
      actionText: this.$t("_action.dismiss") as string,
      type: "is-success",
      indefinite: true
    });

    // Emit event
    this.$emit("onUpgrade");
  }

  //  Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $upgradeData: this.providerData(),
        $upgradeMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("upgradeData")
  public providerData(): IUpmindUpgradeProviderData {
    return {
      availableLicenses: this.availableLicenses,
      availableOptions: this.availableOptions,
      currentLicense: this.currentLicense,
      currentLicenseOptionIds: this.currentLicenseOptionIds,
      dryRunInvoice: this.dryRunInvoice,
      featureFlags: this.featureFlags,
      hasFreeLicense: this.hasFreeLicense,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading
    };
  }

  @Provide("upgradeMethods")
  public get providerMethods(): IUpmindUpgradeProviderMethods {
    return {
      reloadData: this.reloadData,
      setIsProcessing: val => (this.isProcessing = val),
      confirmUpgrade: this.confirmUpgrade
    };
  }
}
